/**
 * <s-product-slider> — one-at-a-time product image slider.
 *
 * Markup:
 *   [data-slider-track]   horizontal scroll-snap track
 *   [data-slide]          each slide; data-media-id holds the media id
 *   [data-slider-prev] / [data-slider-next]  arrow buttons
 *   [data-slider-dot]     dot buttons; data-index holds the slide index
 *   script[data-variant-media] JSON map of variant id -> media id
 * When the selected size changes (input[name="id"] in the product form),
 * the slider moves to that size's photo if it has one.
 */
class SProductSlider extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #dots = [];
  #variantMedia = {};
  #lastVariant = '';
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-slide]')];
    this.#dots = [...this.querySelectorAll('[data-slider-dot]')];

    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.goTo(this.#current() - 1), opts);
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.goTo(this.#current() + 1), opts);
    for (const dot of this.#dots) {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)), opts);
    }
    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    }, { ...opts, passive: true });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.#current() + (this.#isRtl() ? 1 : -1)); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.#current() + (this.#isRtl() ? -1 : 1)); }
    }, opts);

    this.#watchVariant();
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #isRtl() {
    return getComputedStyle(this.#track).direction === 'rtl';
  }

  #current() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  goTo(index, smooth = true) {
    const total = this.#slides.length;
    if (!total) return;
    const target = (index + total) % total;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = this.#slides[target].offsetLeft - this.#track.offsetLeft;
    this.#track.scrollTo({ left, behavior: smooth && !reduce ? 'smooth' : 'auto' });
  }

  #update() {
    const index = this.#current();
    this.#dots.forEach((dot, i) => {
      const active = i === index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.toggleAttribute('data-active', active);
    });
    this.#slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      slide.toggleAttribute('inert', i !== index);
    });
  }

  #watchVariant() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;
    const check = () => {
      const input = form.querySelector('input[name="id"]');
      const id = input?.value || '';
      if (!id || id === this.#lastVariant) return;
      const first = this.#lastVariant === '';
      this.#lastVariant = id;
      if (first) return;
      const mediaId = this.#variantMedia[id];
      if (!mediaId) return;
      const index = this.#slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    };
    check();
    this.#observer = new MutationObserver(check);
    this.#observer.observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value'] });
    form.addEventListener('change', () => setTimeout(check, 0), { signal: this.#ac.signal });
  }
}

if (!customElements.get('s-product-slider')) {
  customElements.define('s-product-slider', SProductSlider);
}
